import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  Settings,
  MapPin,
  Users,
  Package,
  AlertCircle,
  DollarSign,
  X,
  Mail,
  Check,
  Loader2,
  ChevronLeft,
} from 'lucide-react'
import { ContainerTask, StatusConfig } from '../../types/container'
import { OperationsTab } from './tabs/OperationsTab'
import { PaymentDocumentsTab } from './tabs/PaymentDocumentsTab'
import { RouteAppointmentsTab } from './tabs/RouteAppointmentsTab'
import { useAppointments } from '../../hooks/useAppointments'
import { useFixPlateFlow } from '../../hooks/useFixPlateFlow'
import { useGateFeeFlow } from '../../hooks/useGateFeeFlow'
import { useRescheduleFlow } from '../../hooks/useRescheduleFlow'
import { useCoscoOnhireFlow } from '../../hooks/useCoscoOnhireFlow'
import { normalizeScac } from '../../constants/shippingLines'
import { toast } from 'sonner'
import appointmentApi from '../../services/appointmentApi'
import type { UpdateAppointmentInput } from '../../types/appointment'
import { PartiesRelatedTab } from './tabs/PartiesRelatedTab'
import { PausedPanel } from './panels/PausedPanel'
import { TaskModalFooter, ModalTriggers } from './TaskModalFooter'

interface TaskDetailModalProps {
  task: ContainerTask
  open: boolean
  onClose: () => void
  onSave: (task: ContainerTask) => Promise<void>
  statusConfig: Record<string, StatusConfig>
  allContainerTasks: ContainerTask[]
  /**
   * Re-pull the list behind this modal. Fired by actions that write to the
   * server on their own (reroute) rather than through onSave — the board holds
   * its own snapshot of the row and has no other way to learn the destination
   * moved. Optional: read-only hosts (entity-link preview) leave it off.
   */
  onServerUpdate?: () => void
  /**
   * 打开时停在哪一页。默认 Operations。
   *
   * 派单板那条「这个码头助手约不了，去把预约填上」的路要直接落在 `'route'` ——
   * 落在 Operations 再让人自己找页签，等于把催办变成了猜谜。
   */
  initialTab?: string
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  task,
  open,
  onClose,
  onSave,
  statusConfig,
  allContainerTasks,
  onServerUpdate,
  initialTab,
}) => {
  // Tab state — default to Operations
  const [activeTab, setActiveTab] = useState(initialTab || "operations")

  // Scroll position per tab
  const scrollPositions = useRef<Record<string, number>>({})
  const contentRef = useRef<HTMLDivElement>(null)

  const handleTabSwitch = useCallback((tab: string) => {
    if (contentRef.current) {
      scrollPositions.current[activeTab] = contentRef.current.scrollTop
    }
    setActiveTab(tab)
  }, [activeTab])

  // Restore scroll position when tab changes
  useEffect(() => {
    if (contentRef.current && open) {
      const saved = scrollPositions.current[activeTab] || 0
      contentRef.current.scrollTop = saved
    }
  }, [activeTab, open])

  // Editing state
  const [editingTask, setEditingTask] = useState<ContainerTask>(task)
  const skipNextPropSync = useRef(false)
  // editingTask 这份快照建立时的 status。保存时靠它区分"用户改了状态"和
  // "这份表单已经过期" —— 后者不能把旧状态发回去覆盖别人刚写的。
  const statusBaselineRef = useRef<string>(task.status)

  /**
   * 从关联任务点进来的那条路。
   *
   * 这个弹窗一直是**原地换人**：点关联任务只是把 editingTask 换掉，不叠弹窗 ——
   * 二级弹窗在这个项目的层级上踩过坑，不想再开一层。代价是点进去就回不来，
   * 这里把来路记下来，标题栏左边给一个返回。
   *
   * `baseline` 是当前这条任务未改动的那一份。改不干净会连带一个毛病：原来的
   * 「有没有改过」拿 editingTask 和 `task`（外面传进来那条）比，一旦切到兄弟
   * 任务就永远不相等 —— 什么都没动，关窗口也会弹「要丢弃改动吗」。
   */
  const [navStack, setNavStack] = useState<{ task: ContainerTask; tab: string }[]>([])
  const [baselineTask, setBaselineTask] = useState<ContainerTask>(task)

  // Sync from prop (unless just did a local update)
  useEffect(() => {
    if (skipNextPropSync.current) {
      skipNextPropSync.current = false
      return
    }
    setEditingTask(task)
    setBaselineTask(task)
    setNavStack([])
    statusBaselineRef.current = task.status
  }, [task])

  // Resume state (shared between footer and PausedPanel in OperationsTab)
  const [isResuming, setIsResuming] = useState(false)

  // Footer ref for modal triggers
  const footerRef = useRef<ModalTriggers>(null)

  // Fetch appointments from the new appointments table
  const taskDbId = editingTask.id ? parseInt(editingTask.id) : null
  const { appointments, refresh: refreshAppointments } = useAppointments({
    taskId: taskDbId && !isNaN(taskDbId) ? taskDbId : null,
    enabled: open,
  })

  const handleContainerChange = useCallback((updates: Partial<ContainerTask>) => {
    setEditingTask(prev => ({ ...prev, ...updates }))
    // Refresh appointments table data when appointment fields change
    const apptFields = ['portAppointment', 'portAppointmentEnd', 'portAppointmentStatus',
      'warehouseAppointment', 'warehouseAppointmentStatus', 'appointmentNumber', 'status']
    if (apptFields.some(f => f in updates)) {
      refreshAppointments()
    }
  }, [refreshAppointments])

  // Dirty check + discard confirmation. Compared against the task currently on
  // screen, which is not always the one the parent handed us — see navStack.
  const isDirty = useCallback(() => {
    return JSON.stringify(editingTask) !== JSON.stringify(baselineTask)
  }, [editingTask, baselineTask])

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  /**
   * 用户按下「丢弃改动」之后要做的那件事：关窗口、退回上一条、或者去另一条。
   *
   * 换任务和关窗口一样会丢掉没保存的改动，所以走同一道确认 —— 原来点关联任务
   * 是**一声不吭**换掉的，填了一半的表单就这么没了。
   */
  const [discardIntent, setDiscardIntent] = useState<
    { type: 'close' } | { type: 'back' } | { type: 'open'; task: ContainerTask }
  >({ type: 'close' })

  const goBack = useCallback(() => {
    setNavStack(prev => {
      if (prev.length === 0) return prev
      const previous = prev[prev.length - 1]
      setEditingTask(previous.task)
      setBaselineTask(previous.task)
      statusBaselineRef.current = previous.task.status
      setActiveTab(previous.tab)
      return prev.slice(0, -1)
    })
  }, [])

  /** 点开同一单的另一程：记下来路，原地换人。 */
  const goToRelatedTask = useCallback((next: ContainerTask) => {
    setNavStack(prev => [...prev, { task: baselineTask, tab: activeTab }])
    setEditingTask(next)
    setBaselineTask(next)
    statusBaselineRef.current = next.status
    setActiveTab('operations')
  }, [baselineTask, activeTab])

  const handleBack = useCallback(() => {
    if (isDirty()) {
      setDiscardIntent({ type: 'back' })
      setShowDiscardConfirm(true)
      return
    }
    goBack()
  }, [isDirty, goBack])

  const handleOpenRelatedTask = useCallback((next: ContainerTask) => {
    if (isDirty()) {
      setDiscardIntent({ type: 'open', task: next })
      setShowDiscardConfirm(true)
      return
    }
    goToRelatedTask(next)
  }, [isDirty, goToRelatedTask])

  const handleCloseModal = useCallback(() => {
    if (isDirty()) {
      setDiscardIntent({ type: 'close' })
      setShowDiscardConfirm(true)
    } else {
      setEditingTask(task)
      onClose()
    }
  }, [isDirty, task, onClose])

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false)
    if (discardIntent.type === 'back') {
      setEditingTask(baselineTask)
      goBack()
      return
    }
    if (discardIntent.type === 'open') {
      setEditingTask(baselineTask)
      goToRelatedTask(discardIntent.task)
      return
    }
    setEditingTask(task)
    onClose()
  }, [discardIntent, baselineTask, goBack, goToRelatedTask, task, onClose])

  // Update an appointment (status change, etc.) and refresh
  const handleAppointmentUpdate = useCallback(async (appointmentId: number, data: UpdateAppointmentInput) => {
    await appointmentApi.update(appointmentId, data)
    await refreshAppointments()
  }, [refreshAppointments])

  // B3 Fix Plate flow — wires the truck-icon button on each appointment
  // card. Hook listens for the appointment_updated socket the backend
  // emits on completeFixPlate; we additionally refresh local state so the
  // green ✅ shows up immediately even if socket is delayed.
  const { startFixPlate } = useFixPlateFlow({
    onCompleted: () => {
      void refreshAppointments()
      // Auto-close the modal after the success toast has had a moment
      // to show. Mirror of the booking-flow auto-close in TaskModalFooter.
      setTimeout(() => { onClose() }, 1500)
    },
  })
  const handleFixPlate = useCallback((appointmentId: number) => {
    void startFixPlate(appointmentId)
  }, [startFixPlate])

  // Gate Fee — pays the terminal on eModal. Unlike Fix Plate this does NOT
  // auto-close the modal: the operator usually wants to look straight at the
  // Invoice tab to see the Port Fee line come on.
  const { startGateFee } = useGateFeeFlow()
  const handleGateFee = useCallback(() => {
    void startGateFee({ taskId: editingTask?.id })
  }, [startGateFee, editingTask?.id])

  // Reschedule flow — same auto-close-on-success behaviour as Fix Plate
  // and the booking flow.
  const { startReschedule } = useRescheduleFlow({
    onCompleted: () => {
      void refreshAppointments()
      setTimeout(() => { onClose() }, 1500)
    },
  })
  const handleReschedule = useCallback((appointmentId: number) => {
    void startReschedule(appointmentId)
  }, [startReschedule])

  // B5 COSCO On-Hire — only shown for COSCO containers (SCAC = COSU).
  // Lifecycle: created → submitted → completed | abandoned. The button in
  // the header reflects the latest request's status; the operator drives
  // each transition manually because COSCO replies via email.
  //
  // Use normalizeScac (the same helper the steamship-line dropdown uses)
  // so the button is consistent with what the operator sees in the
  // Container Information panel — handles "COSU"/"COS"/"CSNE" aliases AND
  // legacy rows where only the full display name "COSCO Shipping" was
  // captured (no shipping_line_code).
  const isCoscoTask =
    normalizeScac(editingTask.shippingLineCode) === 'COSU' ||
    normalizeScac(editingTask.shippingLine) === 'COSU' ||
    /cosco/i.test(editingTask.shippingLine || '')
  const {
    latest: latestCosco,
    startOnhire,
    completeOnhire,
  } = useCoscoOnhireFlow({ taskId: isCoscoTask && taskDbId ? taskDbId : null })
  const [coscoBusy, setCoscoBusy] = useState(false)
  const handleStartCosco = useCallback(async () => {
    setCoscoBusy(true)
    try {
      const r = await startOnhire()
      if (!r.ok && r.error) toast.error(r.error)
    } finally { setCoscoBusy(false) }
  }, [startOnhire])
  const handleCompleteCosco = useCallback(async () => {
    if (!latestCosco) return
    setCoscoBusy(true)
    try { await completeOnhire(latestCosco.id) } finally { setCoscoBusy(false) }
  }, [latestCosco, completeOnhire])

  // ESC key handler (skip if nested modal is open)
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (footerRef.current?.hasOpenNestedModal()) return
        e.stopPropagation()
        handleCloseModal()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, handleCloseModal])

  // Body scroll lock. Restore whatever was there before rather than clearing:
  // this modal can open on top of another one that already locked scrolling
  // (the driver task-history popup does exactly that), and blanking the value
  // on close would unlock the page while that other modal is still up.
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previousOverflow }
  }, [open])

  // Save handler
  const handleSaveAndClose = async () => {
    await onSave({ ...editingTask, statusBaseline: statusBaselineRef.current })
    onClose()
  }

  // Construct driver object for RouteAppointmentsTab (memoized to prevent child re-renders)
  const driverObj = useMemo(() => ({
    id: editingTask.id || "1",
    name: editingTask.driver || "Unassigned",
    phone: editingTask.driverPhone || "(555) 000-0000",
    // The truck's plate — NOT the driver's licence number. These were swapped
    // here, so the driver panel showed a driving licence next to a truck icon
    // while Fix Plate pushed the real plate to eModal: one task, two different
    // "plates" on screen, and no way to tell which one was going to the
    // terminal. Everywhere else in the app plateLicense means trucks.license_plate.
    plateLicense: editingTask.truckPlate || "",
    company: "Dispatch Company",
    avatar: editingTask.driverAvatar || "/placeholder.svg?height=64&width=64",
    tasks: [
      { id: "1", name: "Current Task", status: "in-progress" as const },
    ],
    userId: editingTask.driverUserId,
  }), [editingTask.id, editingTask.driver, editingTask.driverPhone, editingTask.truckPlate, editingTask.driverAvatar, editingTask.driverUserId])

  // Memoize filtered tasks to prevent re-renders of RouteAppointmentsTab
  const filteredContainerTasks = useMemo(
    () => allContainerTasks.filter(c => c.driver === editingTask.driver),
    [allContainerTasks, editingTask.driver]
  )

  if (!open) return null

  // Tab definitions
  const tabs = [
    { key: 'operations', label: 'Operations', icon: Settings },
    { key: 'route', label: 'Route & Appt', icon: MapPin },
    { key: 'payment', label: 'Docs & Payment', icon: DollarSign },
    { key: 'parties', label: 'Parties & Related', icon: Users },
  ]

  return (
    <>
      {createPortal(
        // Above the dispatch board's own overlays (staging bar z-50, review
        // panel z-60): this can now be opened from inside the review panel, and
        // at z-50 it opened behind it — the dispatcher would press the details
        // button and see nothing happen. Nothing else on the board sits here, so
        // it is only ever on top of whatever it was opened from.
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[1400px] h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* 来路 —— 从关联任务点进来的才有。没有来路时整块不画，
                      标题不会因为多一行字跳动。 */}
                  {navStack.length > 0 && (
                    <button
                      onClick={handleBack}
                      className="flex items-center gap-1 px-2 py-1 -ml-2 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-white/60 transition-colors cursor-pointer"
                      title={`Back to ${navStack[navStack.length - 1].task.taskId}`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {navStack[navStack.length - 1].task.taskId}
                    </button>
                  )}
                  <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Task Details</h3>
                    <p className="text-sm text-gray-600 mt-0.5">{editingTask.containerNumber} • {editingTask.taskId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isCoscoTask && (
                    <CoscoOnhireBadge
                      status={latestCosco?.status || null}
                      busy={coscoBusy}
                      onStart={handleStartCosco}
                      onComplete={handleCompleteCosco}
                      ticketUrl={latestCosco?.freshdesk_ticket_url || null}
                    />
                  )}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    statusConfig[editingTask.status]?.bgColor || 'bg-gray-100'
                  } ${statusConfig[editingTask.status]?.textColor || 'text-gray-700'}`}>
                    {statusConfig[editingTask.status]?.label || editingTask.status}
                  </span>
                  <button
                    onClick={handleCloseModal}
                    title="Close"
                    aria-label="Close"
                    className="p-2 hover:bg-white/60 rounded-xl transition-all duration-200"
                  >
                    <X className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Headers */}
            <div className="border-b border-gray-200 px-8 pt-4">
              <div className="flex items-center gap-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabSwitch(tab.key)}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
                      activeTab === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div ref={contentRef} className="flex-1 overflow-y-auto p-8">
              {activeTab === 'operations' && (
                <OperationsTab
                  container={editingTask}
                  onContainerChange={handleContainerChange}
                  onPause={() => footerRef.current?.openPauseModal()}
                  onResume={async () => {
                    await footerRef.current?.resumeTask()
                  }}
                  onUpdateEta={() => footerRef.current?.openUpdateEta()}
                  isResuming={isResuming}
                  taskDbId={taskDbId && !isNaN(taskDbId) ? taskDbId : null}
                  onTaskActionConverted={() => {
                    // The conversion rewrites route_nodes, the order type and the
                    // salary draft. Refresh only — writing the modal's pre-conversion
                    // copy back would undo it and bump the optimistic-lock token.
                    refreshAppointments()
                  }}
                  pausedPanel={editingTask.status === 'paused' ? (
                    <PausedPanel
                      task={editingTask}
                      onResume={async () => {
                        await footerRef.current?.resumeTask()
                      }}
                      onReassign={() => footerRef.current?.openReassignModal()}
                      isResuming={isResuming}
                    />
                  ) : undefined}
                />
              )}

              {activeTab === 'route' && (
                <RouteAppointmentsTab
                  container={editingTask}
                  onMakePortAppointment={(nodeIndex) => footerRef.current?.openPortAppointment(nodeIndex)}
                  onMakeWarehouseAppointment={() => footerRef.current?.openWarehouseAppointment()}
                  onReschedulePortAppointment={(nodeIndex) => footerRef.current?.reschedulePortAppointment(nodeIndex)}
                  onRescheduleWarehouseAppointment={() => footerRef.current?.rescheduleWarehouseAppointment()}
                  onFixPlate={handleFixPlate}
                  onGateFee={handleGateFee}
                  onReschedule={handleReschedule}
                  onReroute={() => footerRef.current?.openRerouteModal()}
                  onStoreToYard={() => footerRef.current?.openStoreToYard()}
                  onCancelStoreToYard={() => footerRef.current?.cancelStoreToYard()}
                  onReleaseNextLeg={() => footerRef.current?.releaseNextLeg()}
                  onSendToPCC={() => footerRef.current?.openSendToPCC()}
                  onContainerChange={handleContainerChange}
                  driver={driverObj}
                  allContainerTasks={filteredContainerTasks}
                  appointments={appointments}
                  onAppointmentUpdate={handleAppointmentUpdate}
                />
              )}

              {activeTab === 'payment' && (
                <PaymentDocumentsTab
                  container={editingTask}
                  paymentInfo={{
                    routePrice: 0,
                    taskType: editingTask.taskType || 'N/A',
                    waitingHourlyRate: 0,
                    waitingDuration: 0,
                    extraPay: 0,
                  }}
                  onContainerChange={handleContainerChange}
                  onImageClick={(url) => window.open(url, '_blank')}
                />
              )}

              {activeTab === 'parties' && (
                <PartiesRelatedTab
                  container={editingTask}
                  allContainerTasks={allContainerTasks}
                  onTaskClick={handleOpenRelatedTask}
                />
              )}
            </div>

            {/* Footer */}
            <TaskModalFooter
              ref={footerRef}
              task={editingTask}
              originalTask={task}
              onContainerChange={handleContainerChange}
              onSaveAndClose={handleSaveAndClose}
              onCancel={handleCloseModal}
              // Booking flow auto-dismiss: skip the dirty-form discard
              // prompt because the booking succeeded server-side and there
              // is no pending edit state worth preserving.
              onForceClose={onClose}
              skipNextPropSync={skipNextPropSync}
              isResuming={isResuming}
              setIsResuming={setIsResuming}
              appointments={appointments}
              onServerUpdate={onServerUpdate}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Unsaved Changes Confirmation Dialog — kept below the COSCO badge */}
      {showDiscardConfirm && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" onClick={() => setShowDiscardConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Unsaved Changes</h4>
                <p className="text-sm text-gray-500">Your changes have not been saved yet.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              {discardIntent.type === 'close'
                ? 'Are you sure you want to close? All unsaved changes will be lost.'
                : `Are you sure you want to leave ${editingTask.taskId}? All unsaved changes will be lost.`}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Keep Editing
              </button>
              <button
                onClick={handleConfirmDiscard}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

interface CoscoOnhireBadgeProps {
  status: 'created' | 'submitted' | 'completed' | 'abandoned' | null
  busy: boolean
  onStart: () => void
  onComplete: () => void
  ticketUrl: string | null
}

const CoscoOnhireBadge: React.FC<CoscoOnhireBadgeProps> = ({
  status, busy, onStart, onComplete, ticketUrl,
}) => {
  // status === 'created' means the operator opened the Freshdesk form but
  // hasn't solved the CAPTCHA + clicked Submit yet. We render this the same
  // as the idle state visually but show a spinner to signal the in-flight
  // tab, and let the operator re-open if they accidentally closed it.
  if (status === 'completed') {
    return (
      <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 inline-flex items-center gap-1.5">
        <Check className="w-3.5 h-3.5" /> COSCO On-Hire Done
      </span>
    )
  }
  if (status === 'submitted') {
    return (
      <span className="inline-flex items-center gap-2">
        {ticketUrl && (
          <a
            href={ticketUrl}
            target="_blank"
            rel="noreferrer"
            className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
            title="View Freshdesk ticket"
          >
            On-Hire Submitted
          </a>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onComplete}
          className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Mark Reply Received
        </button>
      </span>
    )
  }
  // null / 'abandoned' / 'created' — let the operator (re)launch.
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onStart}
      className="px-3 py-1 rounded-full text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60 inline-flex items-center gap-1.5"
      title={status === 'created' ? 'A previous attempt is still open — click to re-launch' : undefined}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
      {status === 'created' ? 'Re-Open COSCO On-Hire' : 'Request COSCO On-Hire'}
    </button>
  )
}
